import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, IndianRupee, Trash2, Download, FileText, FileSpreadsheet, ArrowUpDown, Filter, Search, Sparkles, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import BulkUploadDialog from '@/components/BulkUploadDialog';
import { useCurrency } from '@/contexts/CurrencyContext';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Resumes() {
  const { formatAmount, symbol: curSymbol } = useCurrency();
  const [resumes, setResumes] = useState([]);
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [formData, setFormData] = useState({
    candidate_name: '', position_id: '', email: '', phone: '', current_ctc: '', expected_ctc: '', status: 'sent'
  });
  const [parsing, setParsing] = useState(false);
  const [pendingCvFile, setPendingCvFile] = useState(null); // CV file to attach after resume submit
  const [uploadingRowId, setUploadingRowId] = useState(null);
  const cvInputRef = useRef(null);
  const rowCvInputRef = useRef(null);
  const rowCvTargetRef = useRef(null);

  const handleAIParse = async (file) => {
    if (!file) return;
    setParsing(true);
    setPendingCvFile(file); // remember so we can attach after the resume is created
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/resume-parser/parse`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const p = res.data.parsed;
      setFormData(prev => ({
        ...prev,
        candidate_name: p.candidate_name || prev.candidate_name,
        email: p.email || prev.email,
        phone: p.phone || prev.phone,
        current_ctc: p.current_ctc ? String(p.current_ctc).replace(/[^0-9.]/g, '') : prev.current_ctc,
        expected_ctc: p.expected_ctc ? String(p.expected_ctc).replace(/[^0-9.]/g, '') : prev.expected_ctc,
      }));
      toast.success('Resume parsed! CV will be attached on submit.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to parse resume'); }
    finally { setParsing(false); }
  };

  const fetchResumes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resumes`, { headers: { Authorization: `Bearer ${token}` } });
      setResumes(response.data);
    } catch (error) { toast.error('Failed to fetch resumes'); }
  };

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/positions`, { headers: { Authorization: `Bearer ${token}` } });
      setPositions(response.data);
    } catch (error) { toast.error('Failed to fetch positions'); }
  };

  useEffect(() => { fetchResumes(); fetchPositions(); }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const canExport = user.role === 'admin' || user.role === 'team_leader';

  const handleDeleteResume = async (id) => {
    if (!window.confirm('Delete this resume?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resumes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resume deleted'); fetchResumes();
    } catch (error) { toast.error('Failed to delete resume'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = { ...formData };
      if (submitData.current_ctc) submitData.current_ctc = parseFloat(submitData.current_ctc);
      if (submitData.expected_ctc) submitData.expected_ctc = parseFloat(submitData.expected_ctc);
      const res = await axios.post(`${API}/resumes`, submitData, { headers: { Authorization: `Bearer ${token}` } });
      const createdId = res.data?.id;
      // If a CV was uploaded for AI parsing, also persist it against this resume.
      if (createdId && pendingCvFile) {
        try {
          const fd = new FormData();
          fd.append('file', pendingCvFile);
          await axios.post(`${API}/resumes/${createdId}/upload-cv`, fd, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Resume added with CV attached');
        } catch (uploadErr) {
          toast.warning('Resume added but CV upload failed: ' + (uploadErr.response?.data?.detail || uploadErr.message));
        }
      } else {
        toast.success('Resume added');
      }
      setOpen(false);
      setFormData({ candidate_name: '', position_id: '', email: '', phone: '', current_ctc: '', expected_ctc: '', status: 'sent' });
      setPendingCvFile(null);
      fetchResumes();
    } catch (error) { toast.error('Failed to add resume'); }
  };

  // Upload a CV against an EXISTING resume row (used for resumes added before
  // CV-attachment was a feature, or when the AI parser was skipped).
  const handleRowCvUpload = async (resumeId, file) => {
    if (!file) return;
    setUploadingRowId(resumeId);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/resumes/${resumeId}/upload-cv`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('CV uploaded');
      fetchResumes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CV upload failed');
    } finally {
      setUploadingRowId(null);
    }
  };

  const handleDownloadCv = async (resume) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/resumes/${resume.id}/cv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = resume.resume_filename || `cv-${resume.candidate_name || resume.id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/resumes/${selectedResume.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated'); setStatusDialogOpen(false); fetchResumes();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/resumes/bulk-status`, { ids: selectedIds, status: bulkStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Updated ${selectedIds.length} resume(s)`); setSelectedIds([]); setBulkStatus(''); fetchResumes();
    } catch { toast.error('Bulk update failed'); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/resumes?format=${format}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `resumes.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === filteredResumes.length ? [] : filteredResumes.map(r => r.id));

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const uniqueCompanies = useMemo(() => [...new Set(resumes.map(r => r.client_name).filter(Boolean))].sort(), [resumes]);
  const uniquePositions = useMemo(() => {
    let src = resumes;
    if (filterCompany !== 'all') src = src.filter(r => r.client_name === filterCompany);
    return [...new Set(src.map(r => r.position_title).filter(Boolean))].sort();
  }, [resumes, filterCompany]);
  const uniqueRecruiters = useMemo(() => {
    let src = resumes;
    if (filterCompany !== 'all') src = src.filter(r => r.client_name === filterCompany);
    if (filterPosition !== 'all') src = src.filter(r => r.position_title === filterPosition);
    return [...new Set(src.map(r => r.recruiter_name).filter(Boolean))].sort();
  }, [resumes, filterCompany, filterPosition]);

  // Reset dependent filters when parent filter changes
  useEffect(() => {
    if (filterCompany !== 'all') {
      const validPositions = resumes.filter(r => r.client_name === filterCompany).map(r => r.position_title);
      if (filterPosition !== 'all' && !validPositions.includes(filterPosition)) setFilterPosition('all');
      const validRecruiters = resumes.filter(r => r.client_name === filterCompany).map(r => r.recruiter_name);
      if (filterRecruiter !== 'all' && !validRecruiters.includes(filterRecruiter)) setFilterRecruiter('all');
    }
  }, [filterCompany]);

  useEffect(() => {
    if (filterPosition !== 'all') {
      let src = resumes;
      if (filterCompany !== 'all') src = src.filter(r => r.client_name === filterCompany);
      const validRecruiters = src.filter(r => r.position_title === filterPosition).map(r => r.recruiter_name);
      if (filterRecruiter !== 'all' && !validRecruiters.includes(filterRecruiter)) setFilterRecruiter('all');
    }
  }, [filterPosition]);

  const filteredResumes = useMemo(() => {
    let data = [...resumes];
    if (searchText) {
      const q = searchText.toLowerCase();
      data = data.filter(r => (r.candidate_name || '').toLowerCase().includes(q) || (r.position_title || '').toLowerCase().includes(q) || (r.client_name || '').toLowerCase().includes(q));
    }
    if (filterCompany !== 'all') data = data.filter(r => r.client_name === filterCompany);
    if (filterPosition !== 'all') data = data.filter(r => r.position_title === filterPosition);
    if (filterRecruiter !== 'all') data = data.filter(r => r.recruiter_name === filterRecruiter);
    if (filterStatus !== 'all') data = data.filter(r => r.status === filterStatus);
    if (filterDateFrom) data = data.filter(r => (r.created_at || '') >= filterDateFrom);
    if (filterDateTo) data = data.filter(r => (r.created_at || '').slice(0, 10) <= filterDateTo);
    data.sort((a, b) => {
      const valA = a[sortField] || '';
      const valB = b[sortField] || '';
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    return data;
  }, [resumes, searchText, filterCompany, filterPosition, filterRecruiter, filterStatus, filterDateFrom, filterDateTo, sortField, sortDir]);

  const SortHeader = ({ field, children }) => (
    <TableHead className="font-bold text-blue-900 cursor-pointer select-none hover:bg-blue-100" onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1">{children}<ArrowUpDown size={12} className={sortField === field ? 'text-blue-600' : 'text-slate-400'} /></span>
    </TableHead>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Resumes</h1>
          <p className="text-slate-600">Track candidate submissions</p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-resumes-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Download size={16} className="mr-2" />Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-resumes-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-resumes-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <BulkUploadDialog
            entityLabel="Resumes"
            templateUrl="/templates/resumes"
            uploadUrl="/resumes/bulk-upload"
            templateFileName="resumes_template.csv"
            helpText="Bulk-add resumes from a CSV. Each resume must reference an EXISTING position by exact title — rows whose position isn't found are reported as errors so you can fix and retry."
            columnSpec={[
              { name: 'candidate_name', required: true, description: 'Full name of the candidate.' },
              { name: 'position_title', required: true, description: 'Must exactly match a position already created in Positions.' },
              { name: 'email', required: false, description: 'Candidate email — used for de-duplication.' },
              { name: 'phone', required: false, description: 'Candidate phone — used for de-duplication.' },
              { name: 'current_ctc', required: false, description: 'Numeric, in tenant base currency (e.g. 1200000).' },
              { name: 'expected_ctc', required: false, description: 'Numeric, in tenant base currency (e.g. 1500000).' },
            ]}
            onSuccess={() => fetchResumes()}
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-resume-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Add Resume</Button>
            </DialogTrigger>
            <DialogContent className="rounded max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">New Resume</DialogTitle></DialogHeader>
              {/* AI Resume Parser */}
              <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <Sparkles size={18} className="text-indigo-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-bold text-indigo-800">AI Resume Parser</p>
                  <p className="text-[10px] text-indigo-600">Upload a PDF/DOC/DOCX/TXT/RTF — auto-fills fields AND attaches the CV (max 10MB)</p>
                </div>
                <input type="file" ref={cvInputRef} accept=".pdf,.docx,.doc,.txt" className="hidden"
                  onChange={e => { if (e.target.files?.[0]) handleAIParse(e.target.files[0]); }} />
                <Button type="button" size="sm" variant="outline" disabled={parsing}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 text-xs font-bold"
                  data-testid="ai-parse-resume-btn" onClick={() => cvInputRef.current?.click()}>
                  {parsing ? <><Loader2 size={14} className="animate-spin mr-1.5" />Parsing...</> : <><Upload size={14} className="mr-1.5" />Upload CV</>}
                </Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Candidate Name</Label>
                  <Input data-testid="resume-candidate-input" value={formData.candidate_name} onChange={(e) => setFormData({ ...formData, candidate_name: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Position</Label>
                  <select data-testid="resume-position-select" value={formData.position_id} onChange={(e) => setFormData({ ...formData, position_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                    <option value="">Select a position</option>
                    {positions.map(pos => <option key={pos.id} value={pos.id}>{pos.title} - {pos.client_name}</option>)}
                  </select>
                  <div className="mt-2">
                    <SearchableSelect value={formData.position_id} onChange={(id) => setFormData({ ...formData, position_id: id })}
                      items={positions} getLabel={(p) => p.title} getSublabel={(p) => p.client_name}
                      emptyLabel="Select a position" placeholder="Search position or client…" testId="resume-position" />
                  </div></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Email</Label>
                    <Input data-testid="resume-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Phone</Label>
                    <Input data-testid="resume-phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Current CTC</Label>
                    <div className="relative mt-2"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">{curSymbol}</span>
                      <Input data-testid="resume-current-ctc-input" type="number" step="0.01" value={formData.current_ctc} onChange={(e) => setFormData({ ...formData, current_ctc: e.target.value })} className="pl-10 rounded border-blue-200" /></div></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Expected CTC</Label>
                    <div className="relative mt-2"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">{curSymbol}</span>
                      <Input data-testid="resume-expected-ctc-input" type="number" step="0.01" value={formData.expected_ctc} onChange={(e) => setFormData({ ...formData, expected_ctc: e.target.value })} className="pl-10 rounded border-blue-200" /></div></div>
                </div>
                <Button type="submit" data-testid="submit-resume-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Add Resume</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-resumes" placeholder="Search candidate, position, client..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="h-9 pl-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company</Label>
          <select data-testid="filter-resume-company" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Companies</option>
            {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Position</Label>
          <select data-testid="filter-resume-position" value={filterPosition} onChange={(e) => setFilterPosition(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Positions</option>
            {uniquePositions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <select data-testid="filter-resume-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Recruiters</option>
            {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
          <select data-testid="filter-resume-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="rejected">Rejected</option>
            <option value="on-hold">On Hold</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
          <Input data-testid="filter-resume-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
          <Input data-testid="filter-resume-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setSearchText(''); setFilterCompany('all'); setFilterPosition('all'); setFilterRecruiter('all'); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); }} className="text-slate-500 text-xs">Clear Filters</Button>
        <span className="text-xs text-slate-500 ml-auto">{filteredResumes.length} of {resumes.length} resumes</span>
      </div>

      {/* Status update dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="rounded">
          <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Update Status</DialogTitle></DialogHeader>
          {selectedResume && (
            <form onSubmit={handleStatusUpdate} className="space-y-4 mt-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <p className="text-sm font-semibold text-blue-900">{selectedResume.candidate_name}</p>
                <p className="text-xs text-slate-600">{selectedResume.position_title}</p>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">New Status</Label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm" required>
                  <option value="">Select status</option><option value="sent">Sent</option><option value="shortlisted">Shortlisted</option><option value="rejected">Rejected</option><option value="on-hold">On Hold</option>
                </select></div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Update Status</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {selectedIds.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3 flex items-center gap-3" data-testid="bulk-action-bar">
          <span className="text-sm font-bold text-blue-900">{selectedIds.length} selected</span>
          <select data-testid="bulk-status-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="h-8 rounded border border-blue-200 bg-white px-2 text-sm">
            <option value="">Set status...</option><option value="sent">Sent</option><option value="shortlisted">Shortlisted</option><option value="rejected">Rejected</option><option value="on-hold">On Hold</option>
          </select>
          <Button size="sm" data-testid="bulk-update-button" onClick={handleBulkStatusUpdate} disabled={!bulkStatus} className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-bold">Apply</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="text-slate-600 text-xs">Clear</Button>
        </div>
      )}

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        {/* Hidden file input used by per-row "Upload CV" buttons */}
        <input
          type="file"
          ref={rowCvInputRef}
          accept=".pdf,.doc,.docx,.txt,.rtf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && rowCvTargetRef.current) {
              handleRowCvUpload(rowCvTargetRef.current, f);
            }
            // Reset so picking the same file twice re-triggers onChange
            e.target.value = '';
          }}
        />
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="w-10"><input type="checkbox" data-testid="select-all-resumes" checked={selectedIds.length === filteredResumes.length && filteredResumes.length > 0} onChange={toggleAll} className="rounded border-blue-300" /></TableHead>
              <SortHeader field="candidate_name">Candidate</SortHeader>
              <TableHead className="font-bold text-blue-900">Position</TableHead>
              <TableHead className="font-bold text-blue-900">Client</TableHead>
              <TableHead className="font-bold text-blue-900">Email</TableHead>
              <TableHead className="font-bold text-blue-900">Phone</TableHead>
              <TableHead className="font-bold text-blue-900">Current CTC</TableHead>
              <TableHead className="font-bold text-blue-900">Expected CTC</TableHead>
              <SortHeader field="status">Status</SortHeader>
              <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
              <SortHeader field="created_at">Submitted</SortHeader>
              <TableHead className="font-bold text-blue-900">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredResumes.length === 0 ? (
              <TableRow><TableCell colSpan={13} className="text-center text-slate-500 py-8">No resumes found</TableCell></TableRow>
            ) : (
              filteredResumes.map((resume) => (
                <TableRow key={resume.id} data-testid={`resume-row-${resume.id}`} className={`hover:bg-blue-50 ${selectedIds.includes(resume.id) ? 'bg-blue-50' : ''}`}>
                  <TableCell><input type="checkbox" data-testid={`select-resume-${resume.id}`} checked={selectedIds.includes(resume.id)} onChange={() => toggleSelect(resume.id)} className="rounded border-blue-300" /></TableCell>
                  <TableCell className="font-semibold text-blue-900">{resume.candidate_name}</TableCell>
                  <TableCell className="text-sm">{resume.position_title}</TableCell>
                  <TableCell className="text-sm">{resume.client_name || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm">{resume.email || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm">{resume.phone}</TableCell>
                  <TableCell className="font-semibold text-teal-700">{resume.current_ctc ? formatAmount(resume.current_ctc) : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="font-semibold text-blue-700">{resume.expected_ctc ? formatAmount(resume.expected_ctc) : <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${resume.status === 'shortlisted' ? 'bg-emerald-100 text-emerald-800' : resume.status === 'rejected' ? 'bg-red-100 text-red-800' : resume.status === 'on-hold' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>{resume.status}</span>
                  </TableCell>
                  <TableCell className="text-sm">{resume.recruiter_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{resume.created_at ? new Date(resume.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {resume.resume_file_path ? (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`download-cv-${resume.id}`}
                          title={`Uploaded: ${resume.resume_uploaded_at ? new Date(resume.resume_uploaded_at).toLocaleString() : '—'}\nFile: ${resume.resume_filename || ''}`}
                          onClick={(e) => { e.stopPropagation(); handleDownloadCv(resume); }}
                          className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 px-2"
                        >
                          <FileText size={14} className="mr-1" />
                          <span className="text-[10px] font-semibold">CV</span>
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm" variant="ghost"
                        data-testid={`upload-cv-${resume.id}`}
                        disabled={uploadingRowId === resume.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          rowCvTargetRef.current = resume.id;
                          rowCvInputRef.current?.click();
                        }}
                        className="text-slate-500 hover:text-blue-700 hover:bg-blue-50 px-2"
                        title="Upload CV (PDF, DOC, DOCX, TXT, RTF — max 10MB)"
                      >
                        {uploadingRowId === resume.id
                          ? <Loader2 size={14} className="animate-spin" />
                          : <><Upload size={14} className="mr-1" /><span className="text-[10px] font-semibold">Add</span></>}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedResume(resume); setNewStatus(resume.status); setStatusDialogOpen(true); }} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50"><Edit2 size={14} /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" data-testid={`delete-resume-${resume.id}`} onClick={() => handleDeleteResume(resume.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
